import React, { useState } from "react";
import { toast } from "react-toastify";
import Layout from "../../components/layout/Layout";
import {
  useAssetTypes,
  useCreateAssetType,
  useUpdateAssetType,
  useDeleteAssetType,
} from "../../api/queries/assetQueries";
import { getApiErrorMessage, getApiErrorStatus } from "../../utils/apiError";
import Dialog from "../../components/ui/Dialog";
import "./assets.css";
import { useCanModify } from "../../hooks/usePermissions";

const EMPTY = { name: "", code: "", description: "", isActive: true };
const CODE_PATTERN = /^[A-Za-z0-9]{2,10}$/;

const TypeFormDialog = ({ type, onClose }) => {
  const isEdit = !!type?.id;
  const [form, setForm] = useState(() =>
    isEdit
      ? {
          name: type.name,
          code: type.code || "",
          description: type.description || "",
          isActive: type.isActive,
        }
      : EMPTY,
  );
  const [error, setError] = useState(null);
  const create = useCreateAssetType();
  const update = useUpdateAssetType();
  const busy = create.isPending || update.isPending;

  const submit = () => {
    if (!form.name.trim()) return setError("Name is required.");
    if (form.code && !CODE_PATTERN.test(form.code))
      return setError("Code must be 2–10 letters or digits.");
    setError(null);
    const body = {
      name: form.name.trim(),
      code: form.code.trim().toUpperCase(),
      description: form.description.trim(),
      isActive: form.isActive,
    };
    if (!body.code) delete body.code; // server defaults to the first 3 letters of the name
    const options = {
      onSuccess: () => {
        toast.success(isEdit ? "Asset type updated." : "Asset type added.");
        onClose();
      },
      onError: (err) =>
        setError(getApiErrorMessage(err, "Could not save the asset type.")),
    };
    if (isEdit) update.mutate({ id: type.id, body }, options);
    else create.mutate(body, options);
  };

  return (
    <Dialog
      title={isEdit ? "Edit asset type" : "Add asset type"}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <button
            type="button"
            className="btn btn-outline-secondary profile-btn"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary profile-btn"
            onClick={submit}
            disabled={busy}
          >
            {busy && (
              <span
                className="spinner-border spinner-border-sm"
                aria-hidden="true"
              ></span>
            )}
            {isEdit ? "Save" : "Add type"}
          </button>
        </>
      }
    >
      {error && (
        <div className="alert alert-danger fs-085 py-2" role="alert">
          {error}
        </div>
      )}
      <div className="row g-3">
        <div className="col-md-8">
          <label htmlFor="type-name" className="profile-label">
            Name <span className="text-danger">*</span>
          </label>
          <input
            id="type-name"
            className="form-control"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. Laptop"
            autoFocus
          />
        </div>
        <div className="col-md-4">
          <label htmlFor="type-code" className="profile-label">
            Tag prefix
          </label>
          <input
            id="type-code"
            className="form-control text-uppercase"
            value={form.code}
            maxLength={10}
            onChange={(e) =>
              setForm({
                ...form,
                code: e.target.value.replace(/[^A-Za-z0-9]/g, ""),
              })
            }
            placeholder={
              form.name
                ? form.name
                    .replace(/[^A-Za-z]/g, "")
                    .slice(0, 3)
                    .toUpperCase() || "LAP"
                : "LAP"
            }
          />
          <div className="text-slate-500 fs-070 mt-1">
            Tags look like {(form.code || "LAP").toUpperCase()}-000042
          </div>
        </div>
        <div className="col-12">
          <label htmlFor="type-desc" className="profile-label">
            Description
          </label>
          <textarea
            id="type-desc"
            className="form-control"
            rows={2}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </div>
        <div className="col-12">
          <div className="form-check form-switch">
            <input
              id="type-active"
              type="checkbox"
              className="form-check-input"
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
            />
            <label htmlFor="type-active" className="form-check-label fs-085">
              Active (can be chosen for new assets)
            </label>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

const DeleteTypeDialog = ({ type, onClose }) => {
  const [inUse, setInUse] = useState(null); // message from a 409
  const [error, setError] = useState(null);
  const remove = useDeleteAssetType();
  const update = useUpdateAssetType();
  const busy = remove.isPending || update.isPending;

  const doDelete = () =>
    remove.mutate(type.id, {
      onSuccess: () => {
        toast.success(`${type.name} deleted.`);
        onClose();
      },
      onError: (err) => {
        if (getApiErrorStatus(err) === 409)
          setInUse(
            getApiErrorMessage(err, "This asset type is used by assets."),
          );
        else
          setError(getApiErrorMessage(err, "Could not delete the asset type."));
      },
    });

  const deactivate = () =>
    update.mutate(
      { id: type.id, body: { isActive: false } },
      {
        onSuccess: () => {
          toast.success(`${type.name} deactivated.`);
          onClose();
        },
        onError: (err) =>
          setError(
            getApiErrorMessage(err, "Could not deactivate the asset type."),
          ),
      },
    );

  return (
    <Dialog
      title={inUse ? "This type is still in use" : `Delete “${type.name}”?`}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <button
            type="button"
            className="btn btn-outline-secondary profile-btn"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          {inUse ? (
            <button
              type="button"
              className="btn btn-warning profile-btn"
              onClick={deactivate}
              disabled={busy || !type.isActive}
            >
              {busy && (
                <span
                  className="spinner-border spinner-border-sm"
                  aria-hidden="true"
                ></span>
              )}
              Deactivate instead
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-danger profile-btn"
              onClick={doDelete}
              disabled={busy}
            >
              {busy && (
                <span
                  className="spinner-border spinner-border-sm"
                  aria-hidden="true"
                ></span>
              )}
              Delete
            </button>
          )}
        </>
      }
    >
      {error && (
        <div className="alert alert-danger fs-085 py-2" role="alert">
          {error}
        </div>
      )}
      {inUse ? (
        <p className="fs-085 mb-0">
          {inUse} Deactivating keeps existing assets as they are but hides the
          type when registering new ones.
          {!type.isActive && " It is already inactive."}
        </p>
      ) : (
        <p className="fs-085 mb-0">This can't be undone.</p>
      )}
    </Dialog>
  );
};

const AssetTypesPage = () => {
  const canModify = useCanModify("assets", { company: true }); // view-only: list only
  const typesQuery = useAssetTypes(true);
  const update = useUpdateAssetType();
  const [editing, setEditing] = useState(null); // {} = new, type = edit
  const [deleting, setDeleting] = useState(null);
  const types = typesQuery.data || [];

  const toggleActive = (t) =>
    update.mutate(
      { id: t.id, body: { isActive: !t.isActive } },
      {
        onSuccess: () =>
          toast.success(
            `${t.name} ${t.isActive ? "deactivated" : "activated"}.`,
          ),
        onError: (err) =>
          toast.error(
            getApiErrorMessage(err, "Could not update the asset type."),
          ),
      },
    );

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <div>
            <h1 className="fw-bold text-dark m-0 page-title">Asset Types</h1>
            <p className="text-muted small mb-0">
              Categories of assets and the prefix used for their tags.
            </p>
          </div>
          {canModify && (
            <button
              type="button"
              className="btn btn-sm px-3 text-white btn-navy h-35px d-flex align-items-center"
              onClick={() => setEditing({})}
            >
              <i className="bi bi-plus-lg me-2"></i> Add type
            </button>
          )}
        </div>

        <div className="glass-table-card">
          <div className="table-responsive">
            <table className="table table-hover mb-0 asset-table">
              <thead className="custom-table-header">
                <tr>
                  <th className="ps-4 py-3">Name</th>
                  <th className="py-3">Code</th>
                  <th className="py-3">Description</th>
                  <th className="py-3">Active</th>
                  <th className="py-3 pe-4 text-end">Actions</th>
                </tr>
              </thead>
              <tbody className="border-top-0">
                {types.map((t) => (
                  <tr
                    key={t.id}
                    className={t.isActive ? "" : "asset-row-deleted"}
                  >
                    <td className="ps-4 fw-semibold">{t.name}</td>
                    <td>
                      <code>{t.code || "—"}</code>
                    </td>
                    <td className="text-slate-600">{t.description || "—"}</td>
                    <td>
                      <div className="form-check form-switch m-0">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          role="switch"
                          checked={t.isActive}
                          onChange={() => toggleActive(t)}
                          disabled={update.isPending}
                          aria-label={`${t.name} active`}
                        />
                      </div>
                    </td>
                    <td className="pe-4 text-end">
                      {canModify ? (
                        <>
                          <div className="d-inline-flex gap-1">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary btn-icon-28"
                              title="Edit"
                              aria-label={`Edit ${t.name}`}
                              onClick={() => setEditing(t)}
                            >
                              <i className="bi bi-pencil-square"></i>
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger btn-icon-28"
                              title="Delete"
                              aria-label={`Delete ${t.name}`}
                              onClick={() => setDeleting(t)}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </>
                      ) : (
                        <span className="fs-070 text-slate-400">View only</span>
                      )}
                    </td>
                  </tr>
                ))}
                {!typesQuery.isLoading && types.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center py-5 text-slate-500">
                      {typesQuery.isError
                        ? getApiErrorMessage(
                            typesQuery.error,
                            "Could not load asset types.",
                          )
                        : "No asset types yet. Add one to start registering assets."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {typesQuery.isLoading && (
            <div className="text-center py-5">
              <span
                className="spinner-border text-primary"
                role="status"
                aria-label="Loading"
              ></span>
            </div>
          )}
        </div>
      </div>

      {editing && (
        <TypeFormDialog
          type={editing.id ? editing : null}
          onClose={() => setEditing(null)}
        />
      )}
      {deleting && (
        <DeleteTypeDialog type={deleting} onClose={() => setDeleting(null)} />
      )}
    </Layout>
  );
};

export default AssetTypesPage;
